import { Breadcrumbs, Container } from "@mui/material";
import { ROUTES } from "constants/routes";
import { useDictionary } from "hooks/use-dictionary";
import Link from "components/link";
import { useRequiredParams } from "hooks/use-required-params";
import { useUrlParams } from "hooks/use-url-params";
import { usePublicProgram } from "hooks/api/use-public-program";
import Layout from "components/layout";
import { interpolate } from "utils/interpolate";
import Table from "pages/common/program-view/components/table/table";
import { useMemo } from "react";
import { DevelopmentCompetencyGroup } from "types/program";
import dayjs from "dayjs";
import { ClassifierValue } from "types/classifier";
import { normalizeExternalUrl } from "utils/normalize-external-url";
import { SafeHtml } from "components/safe-html";

const ProgramViewPage = () => {
  const { id: programId } = useRequiredParams<{ id: string }>();
  const { constructUrlParams } = useUrlParams();
  const { data: program } = usePublicProgram(programId);
  const dict = useDictionary();

  const classifierComponents = useMemo(() => {
    return program?.developmentCompetencyGroupClassifiers?.map((classifier) => {
      let headingText;
      if (classifier.classifier.valueName === DevelopmentCompetencyGroup.CLINICAL_SPECIALTIES) {
        headingText = dict.programs.hoursForCompetenceClinic;
      } else if (classifier.classifier.valueName === DevelopmentCompetencyGroup.GENERAL_CLINICAL_SPECIALTIES) {
        headingText = dict.programs.hoursForCompetenceSpecial;
      } else if (classifier.classifier.valueName === DevelopmentCompetencyGroup.GENERAL_SPECIALTIES) {
        headingText = dict.programs.hoursForCompetenceNotClinic;
      }

      return (
        <div key={classifier.classifier.id} className="grid grid-cols-2">
          <div className="py-2">
            {headingText && <div className="font-bold mb-1">{headingText}</div>}
            <div>
              {interpolate(dict.organizerAdmin.events.durationValue, {
                value: classifier.duration,
              })}
            </div>
          </div>
        </div>
      );
    });
  }, [program, dict]);

  return !program ? null : (
    <Layout>
      <Container>
        <div className="space-y-6 py-10">
          <Breadcrumbs>
            <Link to={ROUTES.home}>{dict.common.breadcrumbsRoot}</Link>
            <Link to={`${ROUTES.home}${ROUTES.programs}${constructUrlParams()}`}>{dict.programs.title}</Link>
            <div>{program?.name}</div>
          </Breadcrumbs>
          <div className="flex justify-between">
            <div className="text-2xl font-bold">{dict.programs.programView.view}</div>
          </div>
          <div className="@container overflow-hidden bg-white">
            <div className="border p-7 space-y-3">
              <div>
                <div className="flex justify-between mb-1 text-sm text-theme-gray300">
                  <div>
                    {program.dateTo
                      ? `${dict.programs.listItemDate.to} ${dayjs(program.dateTo).format("YYYY-MM-DD")}`
                      : dict.programs.programName}
                  </div>
                  <div className="max-sm:hidden">{dict.programs.code}:</div>
                </div>
                <div className="text-[20px] flex justify-between">
                  <div>{program.name}</div>
                  <div className="max-sm:hidden">{program.identification}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-6 py-2">
                <div className="border-l-4 border-theme-primary pl-2 sm:basis-1/4 h-fit px-4">
                  <div className="text-theme-gray300">{dict.certificates.organizer}</div>
                  <div>{program.organizerInstitution.name}</div>
                </div>
              </div>
              <div className="text-[20px] flex justify-between my-1 flex-col space-y-1 sm:hidden">
                <div className="text-sm text-theme-gray300">{dict.programs.code}:</div>
                <div>{program.identification}</div>
              </div>
            </div>
            <div className="border-b border-x">
              <div className="p-6 space-y-6">
                <div className="font-bold text-2xl">{dict.programs.mainInformation}</div>
                <div className="text-[16px] ">
                  <div className="space-y-6">
                    <div>
                      <div className="font-bold mb-1">{dict.programs.identificationNumber}</div>
                      <div>{program.identification}</div>
                    </div>
                    <div>
                      <div className="font-bold mb-1">{dict.programs.organizerExecutingProgram}</div>
                      <div>{program.organizerInstitution.name}</div>
                    </div>
                    <div>
                      <div className="font-semibold mb-2">{dict.organizerAdmin.events.website}</div>
                      <a
                        className="text-theme-primary underline block"
                        href={`${normalizeExternalUrl(program.organizerInstitution.website)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {program.organizerInstitution.website}
                      </a>
                    </div>
                    {program.programFormClassifier && (
                      <div>
                        <div className="font-bold mb-1">{dict.programs.programFormClassifier}</div>
                        <div>{program.programFormClassifier.valueName}</div>
                      </div>
                    )}
                    {(program.confirmationDate || program.dateTo) && (
                      <div className="grid grid-cols-2">
                        {program.confirmationDate && (
                          <div>
                            <div className="font-bold mb-1">{dict.programs.confirmationDate}</div>
                            <div>{dayjs(program?.confirmationDate).format("YYYY-MM-DD")}</div>
                          </div>
                        )}
                        {program.dateTo && (
                          <div>
                            <div className="font-bold mb-1">{dict.programs.dateTo}</div>
                            <div>{program.dateTo ? dayjs(program.dateTo).format("YYYY-MM-DD") : "-"}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {program.description && (
                      <div className="space-y-2">
                        <div className="font-bold">{dict.programs.description}</div>
                        <SafeHtml html={program.description} />
                      </div>
                    )}
                    {program.mandatoryTopicClassifiers && program.mandatoryTopicClassifiers.length > 0 && (
                      <div>
                        <div className="font-bold text-[18px] mb-2">{dict.programs.mandatoryTopicsStructure}</div>
                        <Table>
                          <thead>
                            <tr>
                              <th>
                                <strong>{dict.programs.topic}</strong>
                              </th>
                              <th>
                                <strong>{dict.programs.durationHours}</strong>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {program.mandatoryTopicClassifiers?.map((classifier) => (
                              <tr key={classifier.id}>
                                <td>{classifier.classifier.valueName}</td>
                                <td>
                                  {interpolate(dict.organizerAdmin.events.durationValue, {
                                    value: classifier.duration,
                                  })}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                    {program.evaluationFormClassifier && (
                      <div>
                        <div className="font-bold mb-1">{dict.programs.evaluationFormClassifier}</div>
                        <div>{program.evaluationFormClassifier.valueName}</div>
                      </div>
                    )}
                    {program.developmentProgramMethodOfExecution && (
                      <div>
                        <div className="font-bold mb-1">{dict.programs.developmentProgramMethodOfExecution}</div>
                        <div>{program.developmentProgramMethodOfExecution.valueName}</div>
                      </div>
                    )}
                    {program.theoreticalDistribution && (
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <div className="font-bold mb-1">{dict.programs.theoreticalDistribution}</div>
                          <div>
                            {interpolate(dict.programs.durationValue, {
                              value: program.theoreticalDistribution,
                            })}
                          </div>
                        </div>
                        <div>
                          <div className="font-bold mb-1">{dict.programs.practicalDistribution}</div>
                          <div>
                            {interpolate(dict.programs.durationValue, {
                              value: program.practicalDistribution ?? 0,
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                    {program.duration !== null && program.duration > 0 && (
                      <div>
                        <div className="font-bold mb-1">{dict.programs.duration}</div>
                        <div>{program.duration}</div>
                      </div>
                    )}
                    {program.targetedAudienceClassifiers &&
                      program.targetedAudienceClassifiers.length > 0 &&
                      program.developmentCompetencyGroupClassifiers.length === 0 && (
                        <div>
                          <div className="font-bold mb-1">{dict.programs.targetedClassifiers}</div>
                          <div>
                            {program.targetedAudienceClassifiers?.map((classifier) => classifier.valueName).join(", ")}
                          </div>
                        </div>
                      )}
                    {program.otherTargetedAudienceClassifiers &&
                      program.otherTargetedAudienceClassifiers.length > 0 && (
                        <div>
                          <div className="font-bold mb-1">{dict.programs.otherTargetedAudienceClassifiers}</div>
                          <div>
                            {program.otherTargetedAudienceClassifiers?.map((classifier: ClassifierValue) => classifier.valueName).join(", ")}
                          </div>
                        </div>
                      )}
                    {program.developmentCompetencyGroupClassifiers &&
                      program.developmentCompetencyGroupClassifiers.length > 0 && (
                        <div>
                          <div className="font-bold text-2xl mb-4">{dict.programs.hoursForCompetenceGroups}</div>
                          {program.targetedAudienceClassifiers && program.targetedAudienceClassifiers.length > 0 && (
                            <>
                              <div className="font-bold mb-1">{dict.programs.targetedClassifiers}</div>
                              <div className="mb-2">
                                {program.targetedAudienceClassifiers?.map((classifier) => classifier.valueName).join(", ")}
                              </div>
                            </>
                          )}
                          {classifierComponents}
                        </div>
                      )}
                    {program.practiceTypeSpecializationCompetencyClassifiers &&
                      program.practiceTypeSpecializationCompetencyClassifiers.length > 0 && (
                        <div>
                          <div className="font-bold mb-1">{dict.programs.competenceLearning}</div>
                          <div>
                            {program?.practiceTypeSpecializationCompetencyClassifiers
                              ?.map((x) => x.valueName)
                              .join(", ")}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default ProgramViewPage;
